import React from 'react';

import { formatMoney, mejorCotizacion, labelOrigen, formatDate, labelGenero, formatFranquicia } from './prospectosWebModel';

const Campo = ({ label, value, mono = false, span = 1 }) => (
    <div className={span === 2 ? 'md:col-span-2' : ''}>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{label}</p>
        <p className={`text-sm text-slate-800 break-words ${mono ? 'font-mono text-xs' : 'font-semibold'}`}>
            {value || <span className="text-slate-300">—</span>}
        </p>
    </div>
);

const Bloque = ({ titulo, accent, children }) => (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <header className={`px-5 py-2.5 border-b border-slate-100 ${accent}`}>
            <h3 className="text-[11px] font-black uppercase tracking-widest">{titulo}</h3>
        </header>
        <div className="p-5">{children}</div>
    </section>
);

const fechaLarga = (fecha) =>
    fecha ? fecha.toLocaleString('es-AR', { dateStyle: 'long', timeStyle: 'short' }) : null;

/**
 * Detalle de un lead del cotizador web.
 *
 * Responde las tres preguntas que se hace quien abre el ojo: quién es y qué auto cotizó,
 * de dónde vino, y qué precios llegó a ver. Las cotizaciones ya vienen dentro del
 * documento, así que abrir el modal no dispara ninguna consulta nueva.
 */
const ModalDetalleProspectoWeb = ({ row, onClose }) => {
    if (!row) return null;

    const lead = row.raw || {};
    const visitante = lead.visitante || {};
    const vehiculo = lead.vehiculo || {};
    const ubicacion = lead.ubicacion || {};
    const captura = lead.captura || {};
    const cotizaciones = lead.cotizaciones || [];
    const mejor = mejorCotizacion(cotizaciones);

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-slate-50 w-full max-w-5xl max-h-[92vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden">

                <header className="flex items-start justify-between gap-4 px-7 py-5 bg-white border-b border-slate-200 shrink-0">
                    <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 mb-1">
                            Prospecto web · {labelOrigen(row.origen)}
                        </p>
                        <h2 className="text-xl font-black text-slate-900 tracking-tight truncate">
                            {row.displayNombre}
                        </h2>
                        <p className="text-xs text-slate-500 mt-1">{fechaLarga(row.fechaCreacion)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-slate-400 hover:text-slate-700 p-2 hover:bg-slate-100 rounded-full transition-all cursor-pointer"
                            title="Cerrar"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </header>

                <div className="flex-grow overflow-y-auto p-6 space-y-5 custom-scrollbar">

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <Bloque titulo="Datos del visitante" accent="bg-slate-50 text-slate-600">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Campo label="Nombre" value={visitante.nombre} />
                                <Campo label="Apellido" value={visitante.apellido} />
                                <Campo label="Edad" value={visitante.edad} />
                                <Campo
                                    label={<>DNI calculado<br />(dato no real)</>}
                                    value={visitante.dni}
                                    mono
                                />
                                <Campo
                                    label={<>Nacimiento calculado<br />(dato no real)</>}
                                    value={formatDate(visitante.fechaNacimiento)}
                                />
                                <Campo label="Email" value={visitante.email} span={2} />
                                <Campo label="Teléfono" value={visitante.telefono} mono />
                                <Campo label="Género (forzado)" value={labelGenero(visitante.genero)} />
                            </div>
                        </Bloque>

                        <Bloque titulo="Datos usados para cotizar" accent="bg-sky-50 text-sky-700">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Campo label="Marca" value={vehiculo.marca} />
                                <Campo label="Modelo" value={vehiculo.modelo} />
                                <Campo label="Versión" value={vehiculo.version} span={2} />
                                <Campo label="Año" value={vehiculo.anio} />
                                <Campo label="Uso" value={vehiculo.uso} />
                                <Campo label="0 km" value={vehiculo.esCeroKm ? 'Sí' : 'No'} />
                                <Campo label="GNC" value={vehiculo.tieneGnc ? 'Sí' : 'No'} />
                                <Campo label="Patente" value={vehiculo.patente} mono />
                                <Campo label="Código postal" value={ubicacion.codigoPostal} mono />
                                <Campo label="Localidad" value={ubicacion.localidad} />
                                <Campo label="Provincia" value={ubicacion.provincia} />
                            </div>
                        </Bloque>
                    </div>

                    <Bloque titulo="Origen de la visita" accent="bg-indigo-50 text-indigo-700">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">URL utilizada</p>
                            {captura.url ? (
                                <code className="block text-xs font-mono text-indigo-700 bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 py-2 break-all">
                                    {captura.url}
                                </code>
                            ) : (
                                <span className="text-slate-300 text-sm">—</span>
                            )}
                        </div>
                    </Bloque>

                    <Bloque titulo={`Cotizaciones mostradas al visitante (${cotizaciones.length})`} accent="bg-emerald-50 text-emerald-700">
                        {cotizaciones.length === 0 ? (
                            <div className="text-center py-8 px-4">
                                <p className="text-sm font-bold text-slate-500">
                                    Ninguna compañía devolvió una cotización.
                                </p>
                                {lead.motivoSinCotizaciones && (
                                    <p className="text-xs text-slate-400 mt-2 max-w-md mx-auto">
                                        {lead.motivoSinCotizaciones}
                                    </p>
                                )}
                                <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mt-4">
                                    El visitante se fue sin ver ningún precio
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px]">
                                    <thead>
                                        <tr className="border-b border-slate-200">
                                            {['Compañía', 'Cobertura', 'Suma asegurada', 'Franquicia', 'Premio mensual'].map((h, i, todas) => (
                                                <th
                                                    key={h}
                                                    className={`pb-2 text-[10px] font-black uppercase tracking-widest text-slate-400 ${i === 0 ? 'text-left' : i === todas.length - 1 ? 'text-right' : 'text-center'}`}
                                                >
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cotizaciones.map((cot, index) => {
                                            const esMejor = mejor && cot === mejor;
                                            return (
                                                <tr
                                                    key={`${cot.compania}-${index}`}
                                                    className={`border-b border-slate-100 last:border-0 ${esMejor ? 'bg-emerald-50/50' : ''}`}
                                                >
                                                    <td className="py-3 text-sm font-bold text-slate-800 uppercase">
                                                        {cot.compania}
                                                    </td>
                                                    <td className="py-3 text-center text-xs font-bold text-slate-600 uppercase whitespace-pre-line">{cot.cobertura || '-'}</td>
                                                    <td className="py-3 text-center text-xs text-slate-600 tabular-nums">{formatMoney(cot.sumaAsegurada)}</td>
                                                    <td className="py-3 text-center text-xs text-slate-600 tabular-nums">{formatFranquicia(cot)}</td>
                                                    <td className={`py-3 text-right text-sm tabular-nums ${esMejor ? 'font-black text-emerald-700' : 'font-bold text-slate-800'}`}>
                                                        {formatMoney(cot.premioMensual)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Bloque>
                </div>

                <footer className="px-7 py-4 bg-white border-t border-slate-200 flex justify-between items-center shrink-0">
                    <span className="text-[10px] font-mono text-slate-400">ID: {row.id}</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-full border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 text-xs uppercase tracking-widest transition-all active:scale-95 cursor-pointer"
                    >
                        Cerrar
                    </button>
                </footer>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
};

export default ModalDetalleProspectoWeb;